import { forwardRef } from "react";

import { Box, Stack } from "@mui/material";

import { FormDivider } from "@/forms-design-system/FormDivider";
import { useMediaQuery } from "@/hooks/useMediaQuery";

type ThemedItemBlockLayoutProps = {
  id: string;
  active?: boolean;
  isSoldOut?: boolean;
  /* When highlighted, the early bird chip will appear at the top of the ticket (for memberships) */
  highlightEarlyBirdStatus?: boolean;
  slots: {
    image?: React.ReactNode;
    title: React.ReactNode;
    description?: React.ReactNode;
    earlyBirdInfo?: React.ReactNode;
    bidBadge?: React.ReactNode;
    remainingItemsInfo?: React.ReactNode;
    renewalInfo?: React.ReactNode;
    price: React.ReactNode;
    priceCaption?: React.ReactNode;
    callToAction: React.ReactNode;
    amountInput?: React.ReactNode;
    additionalQuestions?: React.ReactNode;
    groupTicketCaption?: React.ReactNode;
  };
};

export const ThemedItemBlockLayout = forwardRef<HTMLDivElement, ThemedItemBlockLayoutProps>(
  ({ id, active = false, isSoldOut = false, highlightEarlyBirdStatus = false, slots, ...rest }, ref) => {
    const { isSmallScreen } = useMediaQuery();
    return (
      <Box
        ref={ref}
        {...rest}
        id={id}
        borderRadius={1}
        padding={{ xs: 1.5, md: 3 }}
        border={(theme) =>
          // TODO: border should be active always on large screens
          active || !isSmallScreen
            ? `1px solid ${theme.palette.formsComponents.divider.borderColor}`
            : `1px solid transparent`
        }
        sx={(theme) => ({
          opacity: isSoldOut ? 0.4 : undefined,
          transition: "border-color 300ms ease-in", // Add transitions for smooth effects
          ...(active ? { backgroundColor: theme.palette.forms.surface.supershy } : {}),
        })}
        data-test="themed-item-block-card"
      >
        {highlightEarlyBirdStatus && slots.earlyBirdInfo && <Box marginBlockEnd={1}>{slots.earlyBirdInfo}</Box>}
        <Stack direction={{ xs: "column", md: "row" }} alignItems="flex-start" gap={{ xs: 1, md: 2.5 }}>
          {slots.image && (
            <Box flexShrink={{ md: 0 }} sx={{ maxWidth: isSmallScreen ? "100%" : undefined }}>
              {slots.image}
            </Box>
          )}
          <Stack gap={2.5} width="100%" flexGrow={{ md: 1 }}>
            <Box
              display={{ md: "grid" }}
              gridTemplateColumns={{ md: "1fr auto" }}
              gridTemplateRows={{ md: "auto" }}
              gridTemplateAreas={{
                md: `"title callToAction"
                "description description"
                "additionalQuestions additionalQuestions"`,
              }}
              columnGap={{ md: 1 }}
            >
              <Stack gridArea="title" direction="column" gap={0.5}>
                {/* larger margin between bid badge and title only*/}
                <Stack gap={1}>
                  {slots.bidBadge}
                  {slots.title}
                </Stack>
                <Stack direction="row" rowGap={0.5} columnGap={1} alignItems="center" flexWrap="wrap">
                  {slots.price}
                  {slots.priceCaption}
                  {!highlightEarlyBirdStatus && slots.earlyBirdInfo}
                  {slots.remainingItemsInfo}
                  {!!slots.renewalInfo && (
                    <Stack direction="row" gap={1}>
                      <FormDivider orientation="vertical" flexItem />
                      {slots.renewalInfo}
                    </Stack>
                  )}
                </Stack>
                {slots.amountInput}
                {slots.groupTicketCaption}
              </Stack>

              {slots.description && (
                <Box gridArea="description" marginBlockStart={1}>
                  {slots.description}
                </Box>
              )}
              <Box gridArea="callToAction" marginBlockStart={{ xs: 1.5, md: 0 }}>
                {slots.callToAction}
              </Box>
              {slots.additionalQuestions && (
                <Box gridArea="additionalQuestions" marginBlockStart={2.5}>
                  {slots.additionalQuestions}
                </Box>
              )}
            </Box>
          </Stack>
        </Stack>
      </Box>
    );
  }
);

ThemedItemBlockLayout.displayName = "ThemedItemBlockLayout";
